import React from "react";
import {
  TextField as MuiTextField,
  Select as MuiSelect,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  InputAdornment,
} from "@mui/material";
import { useDesignTokens } from "../../../theme/useDesignTokens";
import { ACTION_BUTTON_HEIGHT, ACTION_BUTTON_MIN_WIDTH } from "../../../constants/uiConstants";

/**
 * Common Input Component
 * Provides consistent form field styling, validation, and error handling
 * Supports TextField, Select, Textarea, and custom types
 */
const FormInput = ({
  id,
  type = "text",
  label,
  name,
  value = "",
  onChange,
  onBlur,
  error = false,
  helperText = "",
  placeholder = "",
  fullWidth = true,
  size = "small",
  disabled = false,
  required = false,
  multiline = false,
  rows = 1,
  slotProps = {},
  onKeyDown,
  options = [], // For select
  renderOption,
  sx = {},
  touched = "false",
  startAdornment = null,
  endAdornment = null,
  // showError = touched && error,
  ...props
}) => {
  const { colors } = useDesignTokens();
  const showError = (touched === "true" ? true : false) && error;
  const selectId = id || name;
  const selectLabelId = label ? `${selectId}-label` : undefined;
  const shouldShrinkSelectLabel = Boolean(label) && Boolean(props.displayEmpty);

  const handleKeyDown = (e) => {
    onKeyDown?.(e);
  };

  const mergedSlotProps = {
    ...slotProps,

    /** Native input element */
    htmlInput: {
      ...(slotProps?.htmlInput || {}),
      className: ["browser-default", slotProps?.htmlInput?.className]
        .filter(Boolean)
        .join(" "),
    },

    /** MUI InputBase (adornments live here) */
    input: {
      ...(slotProps?.input || {}),

      ...(startAdornment && {
        startAdornment: (
          <InputAdornment position="start">{startAdornment}</InputAdornment>
        ),
      }),

      ...(endAdornment && {
        endAdornment: (
          <InputAdornment position="end">{endAdornment}</InputAdornment>
        ),
      }),
    },
  };

  const primaryColor = colors?.primary?.main || "#ED6C02";
  const controlHeight = multiline ? "auto" : `${ACTION_BUTTON_HEIGHT}px`;
  const commonSx = {
    "& .MuiOutlinedInput-root": {
      height: controlHeight === "auto" ? "auto" : ACTION_BUTTON_HEIGHT,
      minHeight: controlHeight === "auto" ? undefined : ACTION_BUTTON_HEIGHT,
      boxSizing: "border-box",
    },
    "& .MuiInputBase-input": {
      boxSizing: "border-box !important",
      borderBottom: "none !important", // Explicitly kill Materialize border
      boxShadow: "none !important", // Explicitly kill Materialize shadow
      margin: "0 !important",
      height: multiline
        ? "auto !important"
        : `${ACTION_BUTTON_HEIGHT}px !important`,
      padding:
        size === "small" ? "8.5px 14px !important" : "16.5px 14px !important",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "1px solid rgba(0, 0, 0, 0.42) !important",
    },
    "& .MuiOutlinedInput-notchedOutline legend": {
      display: "block !important",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: primaryColor + " !important",
      borderWidth: "1px !important",
    },
    "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: primaryColor + " !important",
      borderWidth: "1px !important",
    },
    /* Expanded/open select should keep the same focus highlight */
    "& .Mui-expanded .MuiOutlinedInput-notchedOutline": {
      borderColor: primaryColor + " !important",
      borderWidth: "1px !important",
    },
    "& .Mui-error .MuiOutlinedInput-notchedOutline": {
      borderColor: colors?.error?.main + " !important",
      borderWidth: "1px !important",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: primaryColor + " !important",
    },
    "& .MuiInputLabel-root.Mui-expanded": {
      color: primaryColor + " !important",
    },
    "& .MuiInputLabel-root.Mui-error": {
      color: colors?.error?.main + " !important",
    },
    minWidth: `${ACTION_BUTTON_MIN_WIDTH}px`,
    ...sx, // Allow parent overrides
  };

  // TextField and Textarea
  if (
    type === "text" ||
    type === "number" ||
    type === "email" ||
    type === "password" ||
    type === "textarea"
  ) {
    return (
      <MuiTextField
        fullWidth={fullWidth}
        label={label}
        // id={name}
        id={id || name}
        name={name}
        type={type === "textarea" ? "text" : type}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        error={showError}
        helperText={helperText}
        placeholder={placeholder}
        size={size}
        disabled={disabled}
        required={required}
        multiline={type === "textarea" || multiline}
        rows={type === "textarea" ? rows : multiline ? rows : undefined}
        onKeyDown={onKeyDown ? handleKeyDown : undefined}
        slotProps={mergedSlotProps}
        sx={commonSx}
        {...props}
      />
    );
  }

  // Select/Dropdown
  if (type === "select") {
    return (
      <FormControl
        fullWidth={fullWidth}
        size={size}
        error={showError}
        disabled={disabled}
        sx={commonSx}
      >
        {label && (
          <InputLabel id={selectLabelId} shrink={shouldShrinkSelectLabel || undefined}>
            {label}
          </InputLabel>
        )}
        <MuiSelect
          id={selectId}
          labelId={selectLabelId}
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onKeyDown={onKeyDown ? handleKeyDown : undefined}
          label={label}
          disabled={disabled}
          slotProps={{
            input: {
              className: "browser-default",
            },
          }}
          {...props}
        >
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </MuiSelect>
        {(showError || helperText) && (
          <FormHelperText>{showError ? helperText : helperText}</FormHelperText>
        )}
      </FormControl>
    );
  }

  return null;
};

export default FormInput;
